import { motion } from 'framer-motion';
import { AlertCircle, CreditCard } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const PHASE_CONFIG = {
  ideation: { label: 'Ideation Agent', emoji: '💡' },
  research: { label: 'Research Agent', emoji: '🔍' },
  prd: { label: 'PRD Agent', emoji: '📄' },
  design: { label: 'Design Agent', emoji: '🎨' },
  code: { label: 'Coding Agent', emoji: '💻' },
};

const ChatMessage = ({ role, content, status, isStreaming, isError, isCreditsError, phase, steps }) => {
  const isUser = role === 'user';

  if (isUser) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="chat-message-user"
        className="flex justify-end"
      >
        <div className="max-w-[80%] bg-[#EFF6FF] border border-[#2563EB]/20 rounded-xl px-4 py-2.5">
          <p className="text-sm text-[#0F172A] leading-relaxed">{content}</p>
        </div>
      </motion.div>
    );
  }

  if (isCreditsError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="chat-message-credits-error"
        className="flex gap-3"
      >
        <div className="w-6 h-6 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center flex-shrink-0 mt-0.5">
          <CreditCard className="w-3 h-3 text-amber-500" />
        </div>
        <div className="flex-1 rounded-xl bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-xs font-semibold text-amber-700 mb-1.5">Insufficient Credits</p>
          <p className="text-sm text-amber-600 leading-relaxed">
            The ProductOS API ran out of credits. Please add more credits to continue building.
          </p>
          <a
            href="https://openrouter.ai/settings/credits"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="add-credits-link"
            className="inline-flex items-center gap-1.5 mt-2 text-xs text-amber-600 hover:text-amber-700 underline underline-offset-2 transition-colors font-medium"
          >
            <CreditCard className="w-3 h-3" />
            Add credits on OpenRouter
          </a>
        </div>
      </motion.div>
    );
  }

  if (isError) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="chat-message-error"
        className="flex gap-3"
      >
        <div className="w-6 h-6 rounded-full bg-red-50 border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
          <AlertCircle className="w-3 h-3 text-[#EF4444]" />
        </div>
        <div className="flex-1 rounded-xl bg-red-50 border border-red-200 px-4 py-3">
          <p className="text-xs font-semibold text-red-600 mb-1">Error</p>
          <p className="text-sm text-red-500 leading-relaxed">{content}</p>
        </div>
      </motion.div>
    );
  }

  const phaseConf = phase ? PHASE_CONFIG[phase] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="chat-message-assistant"
      className="flex gap-3"
    >
      <div className="w-6 h-6 rounded-full bg-[#EFF6FF] border border-[#E2E8F0] flex items-center justify-center flex-shrink-0 mt-0.5 text-xs">
        {phaseConf ? phaseConf.emoji : '✨'}
      </div>
      <div className="flex-1 min-w-0">
        {phaseConf && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#2563EB] mb-1.5">
            {phaseConf.label}
          </span>
        )}
        {status && !phase && (
          <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-[#475569] mb-1.5">
            {status}
          </span>
        )}
        {steps?.length > 0 && (
          <div className="mb-2 space-y-0.5">
            {steps.map((step, i) => (
              <div key={i} className="flex items-start gap-1.5 text-[11px] text-[#94A3B8]">
                <span className="mt-1 w-1 h-1 rounded-full bg-[#2563EB]/40 flex-shrink-0" />
                <span className="leading-relaxed">{step}</span>
              </div>
            ))}
          </div>
        )}
        <div className="text-[13px] leading-relaxed text-[#475569] prose prose-slate prose-sm max-w-none [&_p]:mb-2 [&_p:last-child]:mb-0 [&_strong]:text-[#0F172A] [&_h1]:text-[#0F172A] [&_h2]:text-[#0F172A] [&_h3]:text-[#0F172A] [&_code]:text-[#2563EB] [&_code]:bg-[#EFF6FF] [&_code]:px-1 [&_code]:rounded">
          {isStreaming ? (
            <span className="whitespace-pre-wrap">
              {content || ''}
              <span className="inline-block w-[3px] h-[14px] bg-[#2563EB] ml-0.5 animate-pulse rounded-[1px] align-text-bottom" />
            </span>
          ) : content ? (
            <ReactMarkdown>{content}</ReactMarkdown>
          ) : null}
        </div>
      </div>
    </motion.div>
  );
};

export default ChatMessage;
